import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, Crown, Check, Star, Zap, Shield, Clock, 
  Users, Video, Award, ChevronRight, CreditCard, Sparkles, X
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

// New Membership Plans as per requirements
const MEMBERSHIP_PLANS = [
  {
    id: "basic",
    name: "Basic",
    price: 0,
    period: "Forever",
    popular: false,
    color: "gray",
    icon: Users,
    features: [
      "1 profile photo",
      "1 extra photo",
      "Talent discovery access"
    ],
    notIncluded: [
      "View contact details",
      "Chat access",
      "Multiple photos",
      "Intro video",
      "Showreels"
    ]
  },
  {
    id: "premium",
    name: "Premium",
    price: 999,
    period: "Year",
    popular: true,
    color: "violet",
    icon: Zap,
    features: [
      "View contact details of profiles",
      "Chat enabled",
      "Up to 10 photos",
      "1 intro video",
      "2 showreels",
      "Priority support"
    ],
    notIncluded: [
      "Priority listings",
      "Advanced analytics"
    ]
  },
  {
    id: "platinum",
    name: "Platinum",
    subtitle: "Enterprise",
    price: 4999,
    period: "Year",
    popular: false,
    color: "amber",
    icon: Crown,
    features: [
      "Everything in Premium",
      "Higher upload limits (50+ photos)",
      "Wider database access",
      "Priority listings",
      "Direct customer support",
      "Advanced analytics"
    ],
    notIncluded: []
  },
  {
    id: "promoter",
    name: "Promoter",
    subtitle: "Ultimate",
    price: 500000,
    period: "3 Years",
    popular: false,
    color: "gradient",
    icon: Sparkles,
    features: [
      "Full access to entire TalentMeet database",
      "Priority announcement & ad placements",
      "Unlimited use of platform services",
      "Dedicated relationship manager",
      "Priority support for 3 years",
      "Custom integrations",
      "White-label options"
    ],
    notIncluded: []
  }
];

export default function Membership() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [currentPlan, setCurrentPlan] = useState("basic");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      setCurrentPlan(userData.membership?.plan || "basic");
    }
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentPlan(data.membership?.plan || "basic");
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const handleSelectPlan = async (plan) => {
    if (plan.id === currentPlan) {
      toast.info("This is your current plan");
      return;
    }
    
    if (plan.price === 0) {
      toast.info("You cannot downgrade to Basic plan");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/payments/initiate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          plan_id: plan.id,
          amount: plan.price,
          currency: "INR"
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success("Redirecting to payment...");
        // In production, this would redirect to actual payment gateway
        // For now, simulate successful payment
        setTimeout(async () => {
          await fetch(`${API_URL}/api/payments/${data.payment_id}/complete`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success(`Upgraded to ${plan.name}!`);
          setCurrentPlan(plan.id);
          setLoading(false);
        }, 2000);
      }
    } catch (error) {
      toast.error("Payment failed. Please try again.");
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (price === 0) return "Free";
    if (price >= 100000) return `₹${(price / 100000).toFixed(0)} Lakh`;
    return `₹${price.toLocaleString()}`;
  };

  const getCardStyle = (plan) => {
    if (plan.color === "gradient") {
      return "bg-gradient-to-br from-violet-600 via-purple-600 to-pink-600 text-white border-0";
    }
    if (plan.popular) {
      return "border-2 border-violet-500 shadow-lg";
    }
    return "";
  };

  const getIconBg = (plan) => {
    const colors = {
      gray: "bg-gray-100",
      violet: "bg-violet-100",
      amber: "bg-amber-100",
      gradient: "bg-white/20"
    };
    return colors[plan.color] || colors.gray;
  };

  const getIconColor = (plan) => {
    const colors = {
      gray: "text-gray-600",
      violet: "text-violet-600",
      amber: "text-amber-600",
      gradient: "text-white"
    };
    return colors[plan.color] || colors.gray;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate("/talent/dashboard")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg" />
                <div>
                  <span className="font-display text-lg font-bold text-gray-900">Membership</span>
                  <p className="text-xs text-gray-500">Choose your plan</p>
                </div>
              </div>
            </div>
            <Badge className="bg-violet-100 text-violet-700 capitalize">
              <Crown className="w-3 h-3 mr-1" /> Current: {currentPlan}
            </Badge>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-br from-violet-600 via-purple-600 to-pink-600 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Badge className="bg-white/20 text-white mb-4">
            <Sparkles className="w-3 h-3 mr-1" /> Upgrade Your Career
          </Badge>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">
            Choose the Right Plan for You
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto text-sm">
            Get discovered faster, access exclusive opportunities, and take your career to the next level with TalentMeet.
          </p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {MEMBERSHIP_PLANS.map(plan => (
            <Card 
              key={plan.id} 
              className={`relative overflow-hidden transition-all hover:shadow-lg ${getCardStyle(plan)}`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-violet-500 text-white text-xs px-3 py-1 rounded-bl-lg">
                  Most Popular
                </div>
              )}
              
              <CardHeader className="text-center pb-3">
                <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${getIconBg(plan)}`}>
                  <plan.icon className={`w-6 h-6 ${getIconColor(plan)}`} />
                </div>
                <CardTitle className={`text-lg ${plan.color === 'gradient' ? 'text-white' : ''}`}>
                  {plan.name}
                  {plan.subtitle && (
                    <span className={`block text-xs font-normal mt-1 ${plan.color === 'gradient' ? 'text-white/70' : 'text-gray-500'}`}>
                      {plan.subtitle}
                    </span>
                  )}
                </CardTitle>
                <div className="mt-2">
                  <span className={`text-2xl font-bold ${plan.color === 'gradient' ? 'text-white' : ''}`}>
                    {formatPrice(plan.price)}
                  </span>
                  {plan.price > 0 && (
                    <span className={plan.color === 'gradient' ? 'text-white/70' : 'text-gray-500'}>
                      /{plan.period}
                    </span>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <ul className="space-y-2 mb-4">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.color === 'gradient' ? 'text-white' : 'text-green-500'}`} />
                      <span className={plan.color === 'gradient' ? 'text-white/90' : ''}>{feature}</span>
                    </li>
                  ))}
                  {plan.notIncluded.map((feature, i) => (
                    <li key={i} className={`flex items-start gap-2 text-sm ${plan.color === 'gradient' ? 'text-white/50' : 'text-gray-400'}`}>
                      <X className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span className="line-through">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  className={`w-full ${
                    plan.color === 'gradient' 
                      ? 'bg-white text-violet-600 hover:bg-gray-100' 
                      : plan.popular 
                        ? 'bg-violet-600 hover:bg-violet-700' 
                        : ''
                  }`}
                  variant={plan.color === 'gradient' || plan.popular ? "default" : "outline"}
                  onClick={() => handleSelectPlan(plan)}
                  disabled={loading || currentPlan === plan.id}
                  data-testid={`select-${plan.id}`}
                >
                  {currentPlan === plan.id ? 'Current Plan' : loading ? 'Processing...' : 'Upgrade Now'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust Badges */}
        <div className="mt-10 bg-gray-100 rounded-2xl p-6 text-center">
          <h3 className="font-semibold mb-4">Trusted by 10,000+ Talents</h3>
          <div className="flex flex-wrap justify-center gap-6">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Shield className="w-5 h-5 text-green-500" />
              Secure Payments
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="w-5 h-5 text-blue-500" />
              Cancel Anytime
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CreditCard className="w-5 h-5 text-purple-500" />
              Multiple Payment Options
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Award className="w-5 h-5 text-yellow-500" />
              Money Back Guarantee
            </div>
          </div>
        </div>

        {/* Contact for Enterprise */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 text-sm">
            Need a custom plan for your organization?{" "}
            <a href="mailto:enterprise@talentmeet.com" className="text-violet-600 font-medium hover:underline">
              Contact our sales team
            </a>
          </p>
        </div>
      </main>

      {/* Demo Notice */}
      <div className="bg-amber-50 border-t border-amber-200 px-4 py-2 text-center">
        <p className="text-xs text-amber-700">
          <Badge variant="outline" className="mr-2">Demo Mode</Badge>
          Payment gateway is ready for Stripe/Razorpay integration.
        </p>
      </div>
    </div>
  );
}
